import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie,
  XAxis, YAxis,
  Tooltip, Legend,
  CartesianGrid,
} from "recharts";
import { ChartWidget } from "../../api/types";

function safeKey(label: string | null | undefined, idx: number) {
  const k = (label ?? "").trim();
  return k.length ? k : `series_${idx}`;
}

function formatTick(v: any) {
  // se è ISO, mostra solo HH:mm (o fallback al valore)
  const s = String(v ?? "");
  if (s.includes("T")) return s.slice(11, 16); // HH:mm
  return s.length > 12 ? s.slice(0, 12) + "…" : s;
}

function toRows(w: ChartWidget) {
  const keys = w.datasets.map((ds, i) => safeKey(ds.label, i));

  return w.labels.map((label, i) => {
    const row: any = { label };
    w.datasets.forEach((ds, dsIdx) => {
      row[keys[dsIdx]] = ds.data?.[i] ?? 0;
    });
    return row;
  });
}

export function ChartWidgetRenderer({
  widget,
  height = 320,
  mode,
}: {
  widget: ChartWidget;
  height?: number;
  mode?: "stacked" | "grouped";
}) {
  const rows = toRows(widget);

  if (widget.type === "line") {
    const keys = widget.datasets.map((ds, i) => safeKey(ds.label, i));

    return (
      <div style={{ width: "100%", minWidth: 1, height, minHeight: 200 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={200}>
          <LineChart data={rows}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} tickFormatter={formatTick} />
            <YAxis unit={widget.unit ?? undefined} />
            <Tooltip />
            <Legend />
            {widget.datasets.map((ds, i) => (
              <Line
                key={keys[i]}
                type="monotone"
                dataKey={keys[i]}
                name={ds.label ?? keys[i]}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (widget.type === "bar") {
    const stacked = mode === "stacked";
    const keys = widget.datasets.map((ds, i) => safeKey(ds.label, i));

    return (
      <div style={{ width: "100%", minWidth: 1, height, minHeight: 200 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={200}>
          <BarChart data={rows}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} tickFormatter={formatTick} />
            <YAxis unit={widget.unit ?? undefined} />
            <Tooltip />
            <Legend />
            {widget.datasets.map((ds, i) => (
              <Bar
                key={keys[i]}
                dataKey={keys[i]}
                name={ds.label ?? keys[i]}
                stackId={stacked ? "stack" : undefined}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // doughnut
  const data = widget.labels.map((name, i) => ({
    name,
    value: widget.datasets[0]?.data?.[i] ?? 0,
  }));

  return (
    <div style={{ width: "100%", minWidth: 1, height, minHeight: 200 }}>
      <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={200}>
        <PieChart>
          <Tooltip />
          <Legend />
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius="55%"
            outerRadius="80%"
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}